import React, { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Container from './Container';

const ServicesIndustries = () => {
    const containerRef = useRef(null);

    const industries = [
        {
            id: 0,
            title: "FMCG",
            description: "We accelerate product movement from factory to shelf, ensuring faster reach, optimized storage, and seamless distribution to keep your brand always in demand.",
            image: "/images/fmcg.webp",
            imageAlt: "Supermarket aisle with products"
        },
        {
            id: 1,
            title: "Healthcare",
            description: "From temperature-controlled storage to secure last-mile delivery, we handle your healthcare logistics with precision and care, ensuring medicines reach safely and on time.",
            image: "/images/healthcare.webp",
            imageAlt: "Stethoscope on medical charts"
        },
        {
            id: 2,
            title: "Technology",
            description: "We streamline the movement of tech components and finished devices with speed and security, supporting your innovation with reliable, time-sensitive logistics solutions.",
            image: "/images/technology.webp",
            imageAlt: "Circuit board with electronic components",
            highlighted: true
        },
        {
            id: 3,
            title: "Agriculture",
            description: "We connect farms to markets efficiently, ensuring freshness, quality, and timely delivery—bridging the gap between producers and consumers seamlessly.",
            image: "/images/agriculture.webp",
            imageAlt: "Agricultural field with silos"
        },
        {
            id: 4,
            title: "Industrial",
            description: "We power your supply chain with robust logistics for heavy machinery, components, and spare parts—minimizing downtime and maximizing operational efficiency.",
            image: "/images/industrial.webp",
            imageAlt: "Industrial complex with smokestacks"
        }
    ];

    useLayoutEffect(() => {
        gsap.registerPlugin(ScrollTrigger);
        const ctx = gsap.context(() => {
            const cards = containerRef.current?.querySelectorAll('.industry-card');
            if (cards && cards.length) {
                const duration = 0.6;
                const stagger = 0.1;
                const tl = gsap.timeline({
                    scrollTrigger: {
                        trigger: containerRef.current,
                        start: 'top 75%',
                        toggleActions: 'play none none none',
                        once: true
                    },
                    onComplete: () => {
                        // Clear inline opacity and transform after all animations complete so CSS can take over
                        // Use setTimeout to ensure GSAP has fully finished
                        setTimeout(() => {
                            cards.forEach(card => {
                                gsap.set(card, { clearProps: "opacity,transform" });
                            });
                        }, 100);
                    }
                });

                tl.fromTo(
                    cards,
                    {
                        opacity: 0,
                        y: 40
                    },
                    {
                        opacity: 0.7,
                        y: 0,
                        duration: duration,
                        stagger: stagger,
                        ease: 'power2.out'
                    }
                );
            }
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <section className="section services-industries bg-white" ref={containerRef}>
            <Container>
                <div className="industries-header">
                    <div className="container section-badge flex items-center gap-3 text-[16px] md:text-[20px]">
                        <div className="badge-dot"></div>
                        Specialized by Sector
                    </div>
                    <h2 className="text-2xl md:text-3xl lg:text-4xl">Industries We Serve</h2>
                </div>

                <div className="industries-grid">
                    {industries.map((industry) => (
                        <div
                            key={industry.id}
                            className={`industry-card ${industry.highlighted ? 'highlighted' : ''}`}
                        >
                            <div className={`industry-image-wrapper ${industry.highlighted ? 'highlighted' : ''}`}>
                                <img
                                    src={industry.image}
                                    alt={industry.imageAlt}
                                    className={`industry-image ${industry.highlighted ? '' : 'desaturated'}`}
                                    loading="lazy"
                                />
                            </div>
                            <h3 className="industry-title text-base md:text-lg lg:text-xl">{industry.title}</h3>
                            <p className="industry-description text-sm md:text-base">{industry.description}</p>
                        </div>
                    ))}
                </div>
            </Container>
        </section>
    );
};

export default ServicesIndustries;

